import type { RouteObject } from 'react-router-dom';
import { Suspense } from 'react';
import { Navigate } from 'react-router-dom';

import { NotFoundPage } from '@/pages/not-found';
import { LoginPage } from '@/pages/login';
import { OAuthCallbackPage } from '@/pages/oauth-callback';

import { StorageOverviewWithFilters } from '@/pages/dashboard/storage-overview';
import { TransportOverviewWithFilters } from '@/pages/dashboard/transport-overview';
import { ServiceUsersWithFilters } from '@/pages/dashboard/service-users';
import { MappingWithFilters } from '@/pages/dashboard/mapping';
import { ConsignmentsPage } from '@/pages/consignments';
import { ConsignmentCreatePage } from '@/pages/consignment-create';
import { ConsignmentEditPage } from '@/pages/consignment-edit';
import { ServiceRequestsPage } from '@/pages/service-requests';
import { ReceivePage } from '@/pages/receive';
import { ReceiveHistoryPage } from '@/pages/receive-history';
import { ReceiveLossesPage } from '@/pages/receive-losses';
import { DispatchInProgressPage } from '@/pages/dispatch-in-progress';

import { PrivateLayout } from '@/widgets/private-layout';
import { PublicLayout } from '@/widgets/public-layout';

import { RouteNames } from '@/shared/config/routes';
import { AuthGuard } from '@/shared/lib';
import { PageLoader } from '@/shared/ui';
import { ProjectSiteRedirect, ProjectSiteLoader } from '../ui';

export const publicRoutes: RouteObject[] = [
  {
    element: (
      <AuthGuard requireAuth={false}>
        <PublicLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: RouteNames.LOGIN,
        element: <LoginPage />
      },
      {
        element: <OAuthCallbackPage />,
        children: [
          {
            path: RouteNames.AZURE_CALLBACK
          },
          {
            path: RouteNames.CIAM_CALLBACK
          }
        ]
      },
      {
        path: RouteNames.NOT_FOUND,
        element: <NotFoundPage />
      }
    ]
  }
];

export const privateRoutes: RouteObject[] = [
  {
    element: (
      <AuthGuard requireAuth={true}>
        <PrivateLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: RouteNames.HOME,
        element: <ProjectSiteRedirect />
      },
      {
        path: RouteNames.CONSIGNMENTS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ConsignmentsPage />
          </Suspense>
        )
      },
      {
        path: RouteNames.CONSIGNMENT_NEW,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ConsignmentCreatePage />
          </Suspense>
        )
      },
      {
        path: RouteNames.CONSIGNMENT_EDIT,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ConsignmentEditPage />
          </Suspense>
        )
      },
      {
        path: RouteNames.SERVICE_REQUESTS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ServiceRequestsPage />
          </Suspense>
        )
      },
      {
        path: RouteNames.RECEIVE,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceivePage />
          </Suspense>
        )
      },
      {
        path: RouteNames.RECEIVE_HISTORY,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceiveHistoryPage />
          </Suspense>
        )
      },
      {
        path: RouteNames.RECEIVE_LOSSES,
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReceiveLossesPage />
          </Suspense>
        )
      },
      {
        path: RouteNames.DISPATCH_IN_PROGRESS,
        element: (
          <Suspense fallback={<PageLoader />}>
            <DispatchInProgressPage />
          </Suspense>
        )
      },
      {
        path: RouteNames.PROJECT_SITE_HOME,
        element: <ProjectSiteLoader />,
        children: [
          {
            index: true,
            element: <Navigate to="dashboard/storage-overview" replace />
          },
          {
            path: 'dashboard/storage-overview',
            element: (
              <Suspense fallback={<PageLoader />}>
                <StorageOverviewWithFilters />
              </Suspense>
            )
          },
          {
            path: 'dashboard/transport-overview',
            element: (
              <Suspense fallback={<PageLoader />}>
                <TransportOverviewWithFilters />
              </Suspense>
            )
          },
          {
            path: 'dashboard/service-users',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ServiceUsersWithFilters />
              </Suspense>
            )
          },
          {
            path: 'dashboard/mapping',
            element: (
              <Suspense fallback={<PageLoader />}>
                <MappingWithFilters />
              </Suspense>
            )
          },
          {
            path: 'requests',
            element: (
              <Suspense fallback={<PageLoader />}>
                <ConsignmentsPage />
              </Suspense>
            )
          }
        ]
      }
    ]
  }
];
